import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function ReferralRedirect() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  useEffect(() => {
    const to = sp.get("to") || "/register";
    api.get(`/api/referral/${code}?track=true`).then(({ data }) => { localStorage.setItem("sa_referral_code", data.code); if (data.promo_code) localStorage.setItem("sa_promo_code", data.promo_code); })
      .catch(() => {}).finally(() => { window.location.replace(to.startsWith("/") ? to : "/register"); });
  }, [code, sp]);
  return <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mt-32" data-testid="ref-redirect" />;
}

export default function AgentJoinPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { login } = useAuth();
  const [inv, setInv] = useState(null);
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/api/agent-invites/${token}`).then(r => { setInv(r.data); setF(x => ({ ...x, name: r.data.name || "", email: r.data.email || "" })); }).catch(() => setInv(false)); }, [token]);
  const join = async () => {
    setBusy(true);
    try { await api.post(`/api/agent-invites/${token}/join`, f); await login(f.email, f.password); toast.success("Welcome to the team!"); nav("/agent"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (inv === null) return <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mt-32" />;
  if (!inv) return <p className="text-center mt-32 text-slate-500" data-testid="agent-join-invalid">This invite link isn't valid anymore. Ask your rep for a new one.</p>;
  const t = inv.terms;
  const pay = [t.sale_pct ? `${t.sale_pct}% of every sale` : "", t.per_sale ? `$${t.per_sale} per sale` : "", t.per_lead ? `$${t.per_lead} per lead` : ""].filter(Boolean).join(" + ") || "Ask your team leader";
  return (
    <div className="min-h-screen bg-slate-50 pt-28 pb-16 px-4" data-testid="agent-join-page">
      <div className="max-w-md mx-auto bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
        <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center"><Users className="w-6 h-6" /></div>
        <div><h1 className="text-2xl font-extrabold text-slate-900">Join {inv.leader_name}'s referral team</h1><p className="text-sm text-slate-500 mt-1">Share your links, bring leads and earn: <b className="text-emerald-700" data-testid="agent-join-pay">{pay}</b>.</p></div>
        <Input placeholder="Full name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="agent-join-name" />
        <Input type="email" placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="agent-join-email" />
        <Input type="tel" placeholder="Mobile (optional)" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid="agent-join-phone" />
        <Input type="password" placeholder="Create a password (8+ characters)" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} data-testid="agent-join-password" />
        <button disabled={busy || !f.name || !f.email || f.password.length < 8} onClick={join} className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold py-3 rounded-xl" data-testid="agent-join-submit">{busy ? "Joining…" : "Join the team"}</button>
      </div>
    </div>
  );
}
